import { sdk } from '@needful-things-mvp/sdk';

import { USE_MOCK } from './data-mode';
import { unwrap } from './http';
import { delay } from './mock';

export interface ChannelField {
  key: string;
  label: string;
  secret: boolean;
  required: boolean;
  placeholder?: string;
  /** Masked for secret fields; plain otherwise. */
  value: string;
}

export interface NotificationChannel {
  type: string;
  label: string;
  description: string;
  enabled: boolean;
  configured: boolean;
  source: 'database' | 'none';
  fields: ChannelField[];
}

export interface Settings {
  environment: string;
  storage: {
    provider: string;
    bucket: string;
    publicUrl: string;
  };
  oauthProviders: string[];
  notifications: {
    channels: NotificationChannel[];
  };
  defaults: {
    votingPeriodDays: number;
  };
}

export interface SettingsInput {
  defaultVotingDays?: number;
}

export interface ChannelInput {
  enabled?: boolean;
  values?: Record<string, string>;
}

const SECRET_MASK = '••••••••';

function maskSecret(value: string): string {
  if (value.length <= 8) return SECRET_MASK;
  return `${value.slice(0, 4)}${SECRET_MASK}${value.slice(-4)}`;
}

let settings: Settings = {
  environment: 'production',
  storage: {
    provider: 'Cloudflare R2',
    bucket: 'needful-things-mvp-uploads',
    publicUrl: 'https://cdn.needful-things-mvp.com',
  },
  oauthProviders: ['google'],
  notifications: {
    channels: [
      {
        type: 'discord',
        label: 'Discord',
        description: 'Post admin alerts to a channel via a bot.',
        enabled: false,
        configured: false,
        source: 'none',
        fields: [
          {
            key: 'botToken',
            label: 'Bot token',
            secret: true,
            required: true,
            placeholder: 'MTIzNDU2…',
            value: '',
          },
          {
            key: 'channelId',
            label: 'Channel ID',
            secret: false,
            required: true,
            placeholder: '123456789012345678',
            value: '',
          },
        ],
      },
    ],
  },
  defaults: {
    votingPeriodDays: 90,
  },
};

/** `GET /admin/settings`. */
export async function getSettings(): Promise<Settings> {
  if (!USE_MOCK) {
    return unwrap<Settings>(await sdk.api.admin.settings.get());
  }
  await delay();
  return settings;
}

/** `PATCH /admin/settings`. */
export async function updateSettings(input: SettingsInput): Promise<Settings> {
  if (!USE_MOCK) {
    return unwrap<Settings>(await sdk.api.admin.settings.patch(input));
  }
  await delay();
  settings = {
    ...settings,
    defaults: {
      votingPeriodDays: input.defaultVotingDays ?? settings.defaults.votingPeriodDays,
    },
  };
  return settings;
}

/** Mirrors `PUT /admin/settings/notifications/:type`. */
export async function updateNotificationChannel(
  type: string,
  input: ChannelInput,
): Promise<Settings> {
  if (!USE_MOCK) {
    return unwrap<Settings>(await sdk.api.admin.settings.notifications({ type }).put(input));
  }
  await delay();

  const channels = settings.notifications.channels.map((channel) => {
    if (channel.type !== type) return channel;

    const next: Record<string, string> = {};
    for (const field of channel.fields) {
      const provided = input.values?.[field.key];
      // A blank secret means "keep the stored value".
      if (field.secret && (provided === undefined || provided.trim() === '')) {
        next[field.key] = field.value;
        continue;
      }
      const value = provided ?? field.value;
      next[field.key] = field.secret && value ? maskSecret(value) : value;
    }

    const configured = channel.fields
      .filter((field) => field.required)
      .every((field) => Boolean(next[field.key]));

    return {
      ...channel,
      enabled: input.enabled ?? channel.enabled,
      configured,
      source: 'database' as const,
      fields: channel.fields.map((field) => ({ ...field, value: next[field.key] ?? field.value })),
    };
  });

  settings = { ...settings, notifications: { channels } };
  return settings;
}
